import { useApp } from '../store';

/**
 * BUILDS SHIPPED PER DAY — the shape of the shipping cadence.
 *
 * A rate answers "are we keeping up"; this shape answers "what happened", and a
 * fortnight of nothing followed by four in a day is a fact about the work that no
 * average can carry. It lived on Insights while Insights was the only home for a
 * chart; it belongs on Releases, next to the builds it counts.
 *
 * FIXED FOOTPRINT (Vinnie #3). The old version scaled each bar's HEIGHT by its
 * count divided by the busiest day, so the same "2" was a tall bar in a quiet
 * week and a stub in a busy one, and one big day shrank every other bar. Now
 * every bar is the SAME height: how many shipped is read from the number above
 * it and, redundantly, from the fill intensity (four steps of the app blue) — so
 * the chart never rescales when the data changes. A day whose date was
 * reconstructed afterwards carries a hatch, the same distinction the old two-part
 * bar drew. The strip is scoped to the window the screen is showing.
 */

const DAY_MS = 86_400_000;
const MONTHS_W = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
/** A Date → local 'YYYY-MM-DD'. Builds store a local calendar day, so the column
 *  keys and the "today" axis must be read in LOCAL terms too — toISOString() slices
 *  the UTC day, which runs a day ahead every evening west of UTC and made the strip
 *  disagree with the screen's "today" and with the Releases list window. */
const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/** 'YYYY-MM-DD' → '7 Sept' (day + worded month, R16) or with the year. */
function worded(isoDay: string, withYear = false): string {
  const [y, m, d] = isoDay.split('-').map(Number);
  if (!y || !m || !d) return isoDay;
  return `${d} ${MONTHS_W[m - 1]}${withYear ? ` ${y}` : ''}`;
}
/** How many on a day → which of the four intensity steps (none below 1). */
function vclass(n: number): string {
  if (n <= 0) return '';
  if (n === 1) return 'v1';
  if (n === 2) return 'v2';
  if (n === 3) return 'v3';
  return 'v4';
}

export default function BuildCadence({ days = 30, now = Date.now() }: { days?: number; now?: number }) {
  const builds = useApp((s) => s.builds);

  // What shipped on each dated day, split into recorded vs reconstructed.
  const byDate = new Map<string, { n: number; exact: number; approx: number }>();
  for (const b of builds) {
    if (!b.date) continue;
    const e = byDate.get(b.date) ?? { n: 0, exact: 0, approx: 0 };
    e.n += 1;
    if (b.dateInferred) e.approx += 1;
    else e.exact += 1;
    byDate.set(b.date, e);
  }

  // One column per calendar day in the window, newest on the right. Every column
  // is drawn whether or not anything shipped, so the rhythm (and the gaps) reads.
  const end = new Date(now);
  const cols: { key: string; dayLabel: string; n: number; approx: number; weekMark: boolean }[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const dt = new Date(end.getTime() - i * DAY_MS);
    const key = iso(dt);
    const hit = byDate.get(key);
    cols.push({
      key,
      dayLabel: key.slice(8),
      n: hit?.n ?? 0,
      approx: hit?.approx ?? 0,
      // Bold a label every seven days from the window's start, as a week marker.
      weekMark: (days - 1 - i) % 7 === 0,
    });
  }

  const inWindow = cols.reduce((a, c) => a + c.n, 0);
  const startWorded = worded(cols[0].key);
  const endWorded = worded(cols[cols.length - 1].key, true);

  return (
    <section className="panel cad2-panel">
      <h2 className="cad2-title">Builds shipped per day</h2>
      <p className="cad2-sub">
        {inWindow} release{inWindow === 1 ? '' : 's'} in the last {days} days · {startWorded} – {endWorded}
      </p>

      <div className="cad2">
        {cols.map((c) => (
          <div key={c.key} className="cad2-col" title={`${c.key}: ${c.n} release${c.n === 1 ? '' : 's'}`}>
            <span className={`cad2-n ${c.n > 0 ? '' : 'is-zero'}`}>{c.n > 0 ? c.n : '·'}</span>
            <div
              className={`cad2-bar ${vclass(c.n)} ${c.approx > 0 ? 'has-approx' : ''}`}
              aria-hidden="true"
            />
            <span className={`cad2-lab ${c.weekMark ? 'is-week' : ''}`}>{c.dayLabel}</span>
          </div>
        ))}
      </div>

      <div className="cad2-legend">
        <span className="cad2-scale">
          Fewer
          <i className="cad2-sw v1" />
          <i className="cad2-sw v2" />
          <i className="cad2-sw v3" />
          <i className="cad2-sw v4" />
          More
        </span>
        <span className="cad2-scale">
          <i className="cad2-sw v3 has-approx" /> Date reconstructed
        </span>
        <span className="cad2-note">every bar is the same height — the number and the shade show how many</span>
      </div>
    </section>
  );
}
